import { InfoCircleOutlined } from '@ant-design/icons';
import { FishingReport, WaterbodyDetail } from '@omniafishing/core';
import { Popover, Select } from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';
import pluralize from 'pluralize';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ContentWrapper } from '../../components/content_wrapper/content_wrapper';
import { OmniaButton } from '../../components/omnia_button/omnia_button';
import { ScrollToTop } from '../../components/scroll_to_top/scroll_to_top';
import { SeasonGroupIcon } from '../../components/season_icon/season_group_icon';
import { WaterbodyDetailQueryParams } from '../../components/waterbody_detail/waterbody_detail';
import { WaterbodyDetailNearbyFishingReports } from '../../components/waterbody_detail/waterbody_detail_sections/waterbody_detail_fishing_reports/waterbody_detail_nearby_fishing_reports';
import { WaterbodyDetailReportsList } from '../../components/waterbody_detail/waterbody_detail_sections/waterbody_detail_fishing_reports/waterbody_detail_reports_list';
import { WaterbodyDetailHeader } from '../../components/waterbody_detail/waterbody_detail_sections/waterbody_detail_header/waterbody_detail_header';
import { LoadingState } from '../../constants/loading_state';
import { useAfterMountedEffect } from '../../hooks/use_after_mounted_effect';
import { useQueryString } from '../../hooks/use_query_string';
import { useResponsive } from '../../hooks/use_responsive';
import { apiV1 } from '../../lib/api';
import { OmniaQueryParams } from '../../lib/query_string';
import { isCurrentSeasonGroupText } from '../../lib/seasons';
import { formatToIsoDateZ } from '../../lib/time';
import { WebAnalytics } from '../../lib/web_analytics';
import { getSpeciesBySpeciesName } from '../../redux/reference_data';
import styles from './waterbody_fishing_reports_page.less';

export const SORT_OPTIONS = {
  NEWEST: 'newest',
  MOST_RELEVANT: 'most_relevant',
} as const;

export type SortOptions = typeof SORT_OPTIONS[keyof typeof SORT_OPTIONS];

export const sortOptions = [
  { label: 'Most Relevant', value: SORT_OPTIONS.MOST_RELEVANT },
  { label: 'Newest', value: SORT_OPTIONS.NEWEST },
];

interface WaterbodyFishingReportsPageProps {
  fishingReportsNearby: FishingReport[];
  mostRecentFishingReportDate: dayjs.Dayjs | null;
  initialFishingReports: FishingReport[];
  totalFishingReportsCount: number;
  waterbodyDetail: WaterbodyDetail;
}

export const tooltipTitle = 'What does most relevant mean?';
export const tooltipContent = `Reports from all past years within a 60-day time frame.`;

export const WaterbodyFishingReportsPage = (props: WaterbodyFishingReportsPageProps) => {
  const {
    fishingReportsNearby,
    mostRecentFishingReportDate,
    initialFishingReports,
    totalFishingReportsCount,
    waterbodyDetail,
  } = props;
  const { season_groups, species, url_slug } = waterbodyDetail;
  const { isDesktop, isMobile } = useResponsive();
  const { replaceQueryString } = useQueryString();

  const { getCurrentQuery } = useQueryString();
  const currentQuery = getCurrentQuery<WaterbodyDetailQueryParams>();
  const speciesNameQuery = currentQuery[OmniaQueryParams.species];
  const seasonGroupNameQuery = currentQuery[OmniaQueryParams.season_group];
  const speciesBySpeciesName = useSelector(getSpeciesBySpeciesName);

  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.NOT_STARTED);
  const [fishingReports, setFishingReports] = useState<FishingReport[]>(initialFishingReports);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalFilteredReportsCount, setTotalFilteredReportsCount] =
    useState(totalFishingReportsCount);
  const [selectedSeasonGroup, setSelectedSeasonGroup] = useState(null);
  const [selectedSpecies, setSelectedSpecies] = useState(null);
  const [sortBy, setSortBy] = useState<SortOptions>(SORT_OPTIONS.MOST_RELEVANT);

  const fetchFishingReports = async () => {
    try {
      const res = await apiV1.waterbodyFishingReportsFetch(url_slug, {
        species: speciesNameQuery,
        season_group: seasonGroupNameQuery,
        sort: sortBy,
        per_page: 9,
        page: currentPage,
      });
      setFishingReports((prevReports) => prevReports.concat(res.data.data));
      setTotalFilteredReportsCount((res.data.metadata as any).count_filtered);
      setLoadingState(LoadingState.DONE);
    } catch (err) {
      console.error(err);
      setLoadingState(LoadingState.ERROR);
    }
  };

  useAfterMountedEffect(() => {
    fetchFishingReports();
  }, [sortBy, seasonGroupNameQuery, speciesNameQuery, currentPage]);

  useEffect(
    function syncQueryParamsAndState() {
      if (seasonGroupNameQuery !== selectedSeasonGroup?.name) {
        const seasonGroup = season_groups.find((sg) => sg.name === seasonGroupNameQuery);
        setSelectedSeasonGroup(seasonGroup);
      }

      if (speciesNameQuery !== selectedSpecies?.name) {
        setSelectedSpecies(speciesBySpeciesName(speciesNameQuery));
      }
    },
    [seasonGroupNameQuery, speciesNameQuery, sortBy]
  );

  const isSortedByNewest = sortBy === SORT_OPTIONS.NEWEST;

  const onSortChange = (val: SortOptions) => {
    setLoadingState(LoadingState.PENDING);
    setFishingReports([]);
    setCurrentPage(1);
    setSortBy(val);
  };

  const handleClearFilters = () => {
    setLoadingState(LoadingState.PENDING);
    setFishingReports([]);
    setCurrentPage(1);
    replaceQueryString({
      [OmniaQueryParams.species]: null,
      [OmniaQueryParams.season_group]: null,
    });
  };

  const handleLoadMore = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  const handleFilterChange = (type: 'species' | 'season_group', value: string) => {
    setLoadingState(LoadingState.PENDING);
    setFishingReports([]);
    setCurrentPage(1);
    replaceQueryString({
      [OmniaQueryParams[type]]: value,
    });
    WebAnalytics.contentFiltered(type === 'season_group' ? 'season' : 'species', value);
  };

  const lastUpdatedISODate = formatToIsoDateZ(mostRecentFishingReportDate);

  const hasFilteredReports = totalFilteredReportsCount > 0;
  const noWaterbodyFishingReports = totalFishingReportsCount === 0;
  const hasWaterbodyFishingReports = !noWaterbodyFishingReports;

  const speciesOptions = useMemo(() => {
    const waterbodySpeciesSorted = _.sortBy(species, ['recommendations_order']);
    return [
      {
        label: (
          <span className={styles.selectLabel}>
            <span className={styles.selectName}>All Species</span>
          </span>
        ),
        value: null,
      },
    ].concat(
      waterbodySpeciesSorted.map((s) => {
        return {
          label: (
            <span className={styles.selectLabel}>
              <span className={styles.selectName}>
                <img src={s.image} alt={s.img_alt} className={styles.selectImg} />
                {s.display_name}
              </span>
            </span>
          ),
          value: s.name,
        };
      })
    );
  }, [species]);

  const seasonGroupOptions = useMemo(() => {
    const currentSeasonGroup = season_groups.find((sg) => sg.active);
    return [
      {
        label: (
          <span className={styles.selectLabel}>
            <span className={styles.seasonNameLine}>
              <span className={styles.seasonName}>All Seasons</span>
            </span>
          </span>
        ),
        value: null,
      },
    ].concat(
      season_groups.map((sg) => {
        return {
          label: (
            <span className={styles.selectLabel}>
              <span className={styles.seasonNameLine}>
                <SeasonGroupIcon seasonGroupName={sg.name} className={styles.seasonIcon} />
                <span className={styles.seasonName}>{sg.display_name}</span>
                <span className={styles.currentLabel}>
                  {isDesktop
                    ? isCurrentSeasonGroupText(sg, currentSeasonGroup)
                    : currentSeasonGroup.name === sg?.name
                    ? ' Current'
                    : ''}
                </span>
              </span>
            </span>
          ),
          value: sg.name,
        };
      })
    );
  }, [season_groups, isDesktop]);

  return (
    <ScrollToTop>
      <WaterbodyDetailHeader
        headingTag="p"
        lastUpdatedDate={lastUpdatedISODate}
        productToShowInFollowModal={null}
        scrollToFishingReport={() => null}
        showPublishDates
        waterbody={waterbodyDetail}
        navElement={null}
        showBreadcrumbs={false}
        showButtons={false}
      />
      <div className={styles.fullWidthContainer}>
        <ContentWrapper>
          <div className={styles.sectionHeader}>
            <div className={styles.primaryHeadingContainer}>
              <h1>
                {hasWaterbodyFishingReports
                  ? `${waterbodyDetail.primary_name} Fishing Reports`
                  : `Fishing Reports Near ${waterbodyDetail.primary_name}`}
              </h1>
            </div>
            {noWaterbodyFishingReports && (
              <p className={styles.reportCount}>
                <strong>0 Reports</strong> on {waterbodyDetail.primary_name}
              </p>
            )}

            {hasWaterbodyFishingReports && (
              <div className={styles.filtersAndSorts}>
                <div className={styles.dropdownsWrapper}>
                  <p className={styles.filterSortByTag}>
                    <strong>Filter by</strong>
                  </p>
                  <div>
                    <Select
                      value={!selectedSpecies?.name ? null : selectedSpecies.name}
                      placeholder="Select a Species"
                      onChange={(val) => {
                        handleFilterChange('species', val);
                      }}
                      dropdownStyle={{ minWidth: 256 }}
                      getPopupContainer={(trigger: any) => trigger.parentNode}
                      options={speciesOptions}
                    />

                    <Select
                      value={!selectedSeasonGroup?.name ? null : selectedSeasonGroup.name}
                      dropdownStyle={{ minWidth: 195 }}
                      className={styles.seasonSelect}
                      popupClassName={styles.selectDropdown}
                      getPopupContainer={(trigger: any) => trigger.parentNode}
                      dropdownMatchSelectWidth={false}
                      placeholder="Select a Season"
                      onChange={(val) => {
                        handleFilterChange('season_group', val);
                      }}
                      options={seasonGroupOptions}
                    />
                    {(selectedSeasonGroup || selectedSpecies) && isDesktop && (
                      <OmniaButton
                        className={styles.clearFilters}
                        kind="text"
                        size="md"
                        onPress={handleClearFilters}
                      >
                        Clear Filters
                      </OmniaButton>
                    )}
                  </div>
                  {(selectedSeasonGroup || selectedSpecies) && isMobile && (
                    <OmniaButton
                      className={styles.clearFilters}
                      kind="text"
                      size="md"
                      onPress={handleClearFilters}
                    >
                      Clear Filters
                    </OmniaButton>
                  )}
                </div>

                <div className={styles.radiosSectionWrapper}>
                  <p className={styles.filterSortByTag}>
                    <strong>Sort by</strong>
                  </p>
                  <div className={styles.radios}>
                    <div className={styles.radioGroup}>
                      <label
                        htmlFor={sortOptions[0].label}
                        className={styles.radioContainer}
                        onClick={() => onSortChange(sortOptions[0].value)}
                      >
                        <input
                          type="radio"
                          name={sortOptions[0].label}
                          value={sortOptions[0].value}
                          checked={sortBy === sortOptions[0].value}
                        />
                        <span className={styles.radioCustom} />
                        <span className={styles.radioLabel}>{sortOptions[0].label}</span>
                      </label>
                      <div className={styles.tooltip}>
                        <Popover title={tooltipTitle} content={tooltipContent}>
                          <InfoCircleOutlined />
                        </Popover>
                      </div>
                      <label
                        htmlFor={sortOptions[1].label}
                        className={styles.radioContainer}
                        onClick={() => onSortChange(sortOptions[1].value)}
                      >
                        <input
                          type="radio"
                          name={sortOptions[1].label}
                          value={sortOptions[1].value}
                          checked={sortBy === sortOptions[1].value}
                        />

                        <span className={styles.radioLabel}>{sortOptions[1].label}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {(selectedSpecies || selectedSeasonGroup) && (
              <p className={styles.reportFilterBlurb}>
                <strong>
                  {totalFilteredReportsCount} {pluralize('Report', totalFilteredReportsCount)}
                </strong>{' '}
                for your selected filters
              </p>
            )}
            {hasFilteredReports && (
              <p className={styles.sortTag}>
                {isSortedByNewest
                  ? 'Newest reports'
                  : 'Reports from all past years within a 60-day time frame'}
              </p>
            )}
          </div>

          {noWaterbodyFishingReports ? (
            fishingReportsNearby.length > 0 && (
              <WaterbodyDetailNearbyFishingReports
                nearbyReports={fishingReportsNearby}
                waterbody={waterbodyDetail}
                sortedByNewest={isSortedByNewest}
              />
            )
          ) : (
            <WaterbodyDetailReportsList
              fishingReports={fishingReports}
              loadingState={loadingState}
              onLoadMore={handleLoadMore}
              selectedSeasonGroup={selectedSeasonGroup}
              selectedSpecies={selectedSpecies}
              totalFishingReportsCount={totalFishingReportsCount}
              totalFilteredReportsCount={totalFilteredReportsCount}
            />
          )}
        </ContentWrapper>
      </div>
    </ScrollToTop>
  );
};
